import styled from "@emotion/styled";
import React, { useCallback, useState, useEffect, useRef } from "react";
import Text from "@components/Text";
import FieldRow from "@screens/FieldsScreen/FieldsMenu/FieldRow";
import Feature from "ol/Feature";
import type { Feature as GeoJsonFeature } from "geojson";
import {
  getFieldAreaHectares,
  isRecentlyUpdated,
  useFieldsScreenVM,
} from "@screens/FieldsScreen/FieldsScreenVM";
import { observer } from "mobx-react-lite";
import { useFormatArea, useLang } from "@src/hooks/useLang";
import SizedBox from "@components/SizedBox";
import Button from "@components/Button";
import Dialog from "@components/Dialog";
import Select from "@components/Select";
import Checkbox from "@components/Checkbox";
import { ReactComponent as EditIcon } from "@assets/icons/edit.svg";
import { Column, Row } from "@src/components/Flex";
import { ReactComponent as SearchIcon } from "@assets/icons/search.svg";
import { SearchInput } from "@screens/CropsScreen/CropsScreen";
import UploadFromFileDialog from "@screens/FieldsScreen/dialogs/UploadFromFileDialog";
import ImportFromJohnDeereDialog from "@screens/FieldsScreen/dialogs/ImportFromJohnDeereDialog";
import ImportFromCropwiseDialog from "@screens/FieldsScreen/dialogs/ImportFromCropwiseDialog";
import FromTracksDialog from "@screens/FieldsScreen/dialogs/FromTracksDialog";
import { getJohnDeereStatus } from "@services/johnDeereService";
import { getCropwiseStatus } from "@services/cropwiseService";
import { useStores } from "@stores";
import { toast } from "react-toastify";
import { useSeasonId } from "@src/stores/SeasonStore";
import { useFarmsVM } from "@src/stores/FarmStore";
import BulkActionsBar from "@screens/FieldsScreen/FieldsMenu/BulkActionsBar";
import { useRequireSeason } from "@screens/FieldsScreen/FieldsMenu/useRequireSeason";
import { useNavigate } from "react-router-dom";
import { TSortBy } from "@services/fieldsService";

type PendingFieldAction = "draw" | "tracks" | "kml" | "deere" | "cropwise";

const INITIAL_BATCH = 30;
const BATCH_INCREMENT = 30;

interface IProps {
  // Supplied on mobile, where this menu lives in a bottom sheet: picking a
  // field must dismiss the sheet or it stays parked over the map.
  onSelected?: () => void;
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow-x: hidden;
  justify-content: space-between;
`;

const Header = styled.div`
  width: 100%;
  margin-bottom: 12px;
  padding: 0 16px;
  box-sizing: border-box;
`;

const SortRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

const SortChip = styled.button<{ active?: boolean }>`
  border: 1px solid ${({ active }) => (active ? "#68ad18" : "#ccd5d8")};
  background: ${({ active }) => (active ? "#eaf5da" : "#fff")};
  color: ${({ active }) => (active ? "#3d6e0e" : "#899093")};
  font-family: Nunito Sans, sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  padding: 4px 10px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    border-color: #68ad18;
  }
`;


const HARVEST_THRESHOLD = 0.03; // 3%

const FieldItem = styled.div`
  content-visibility: auto;
  contain-intrinsic-size: auto 88px;
  width: 100%;
  box-sizing: border-box;
`;

const SectionHeader = styled.button<{ collapsed?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 16px;
  background: #f5f6f7;
  border: none;
  border-bottom: 1px solid rgba(137, 144, 147, 0.2);
  cursor: pointer;
  font-family: Nunito Sans, sans-serif;
  font-weight: 700;
  font-size: 13px;
  color: #494c5b;
  transition: background 0.15s;

  &:hover {
    background: #eef0f1;
  }
`;

const SectionArrow = styled.span<{ collapsed?: boolean }>`
  display: inline-block;
  transition: transform 0.2s;
  transform: rotate(${({ collapsed }) => (collapsed ? "-90deg" : "0deg")});
  font-size: 10px;
  color: #899093;
`;

const SectionCount = styled.span`
  font-weight: 400;
  color: #899093;
  margin-left: 6px;
`;

/** Positioning context for the import submenu popover. */
const ImportAnchor = styled.div`
  position: relative;
  width: 100%;
  flex-shrink: 0;
`;

/* The submenu floats ABOVE the Import button rather than sitting below it in
   normal flow. The menu column is pinned to the bottom of a 100vh sidebar and
   `body` has `overflow-y: hidden`, so anything laid out under that button
   renders past the viewport edge with no way to scroll to it — the menu opened
   but was simply invisible. */
const ImportSubMenu = styled.div`
  position: absolute;
  bottom: calc(100% + 6px);
  left: 16px;
  right: 16px;
  z-index: 20;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px;
  box-sizing: border-box;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(78, 78, 81, 0.18);
`;

const ImportSubButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #494C5B;
  transition: all 0.15s;

  &:hover {
    border-color: #68ad18;
    background: #f9fdf5;
  }
`;

/** Close a floating menu on a pointer-down outside `ref` or on Escape. */
const useDismiss = (
  open: boolean,
  ref: React.RefObject<HTMLElement>,
  close: () => void
) => {
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (!ref.current?.contains(e.target as Node)) closeRef.current();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeRef.current();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, ref]);
};

/** Placeholder shown while the integration connection check is still in flight,
 *  so the submenu doesn't render short and then visibly grow as entries land. */
const ImportSubNote = styled.div`
  padding: 10px 12px;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: #899093;
`;

// Bulk-select mode toggle. Deliberately styled as a rounded-rect action
// button with an icon — distinct from the pill-shaped sort chips so it doesn't
// read as another sort/filter option.
const SelectModeButton = styled.button<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  border: 1px solid ${({ active }) => (active ? "#68ad18" : "#ccd5d8")};
  background: ${({ active }) => (active ? "#eaf5da" : "#fff")};
  color: ${({ active }) => (active ? "#3d6e0e" : "#494c5b")};
  font-family: Nunito Sans, sans-serif;
  font-weight: 700;
  font-size: 13px;
  line-height: 16px;
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    border-color: #68ad18;
  }
`;

const ViewFieldsMenu: React.FC<IProps> = ({ onSelected }) => {
  const vm = useFieldsScreenVM();
  const sid = useSeasonId();
  const farmsVM = useFarmsVM();
  const navigate = useNavigate();

  const handleSelect = (id: string | number) => {
    vm.selectField(id);
    onSelected?.();
  };
  const lang = useLang();
  const formatArea = useFormatArea();
  const [uploadDialogOpened, setUploadDialogOpened] = useState(false);
  const [johnDeereImportOpened, setJohnDeereImportOpened] = useState(false);
  const [cropwiseImportOpened, setCropwiseImportOpened] = useState(false);
  const [importMenuOpen, setImportMenuOpen] = useState(false);
  const [newFieldMenuOpen, setNewFieldMenuOpen] = useState(false);
  const [fromTracksOpened, setFromTracksOpened] = useState(false);
  const { settingsStore, accountStore } = useStores();
  // Which integrations are actually connected. null = never fetched yet.
  // Entries for John Deere / Cropwise only render when their integration is
  // connected, so the menu never offers an import the user can't complete.
  const [integrationsConnected, setIntegrationsConnected] = useState<{
    deere: boolean;
    cropwise: boolean;
  } | null>(null);
  const [integrationsChecking, setIntegrationsChecking] = useState(false);
  const [connectPromptVisible, setConnectPromptVisible] = useState(false);
  const [bulkDeleteConfirmVisible, setBulkDeleteConfirmVisible] = useState(false);
  const [bulkCompleteConfirmVisible, setBulkCompleteConfirmVisible] = useState(false);
  const [bulkCropDialogVisible, setBulkCropDialogVisible] = useState(false);
  const [bulkCropId, setBulkCropId] = useState("");
  const [bulkExportDialogVisible, setBulkExportDialogVisible] = useState(false);
  const [bulkMoveFarmDialogVisible, setBulkMoveFarmDialogVisible] = useState(false);
  const [bulkFarmId, setBulkFarmId] = useState("");
  const [harvestedCollapsed, setHarvestedCollapsed] = useState(false);
  const [notHarvestedCollapsed, setNotHarvestedCollapsed] = useState(false);

  const runFieldAction = (action: PendingFieldAction) => {
    if (action === "draw") vm.handleFieldCreate();
    else if (action === "tracks") setFromTracksOpened(true);
    else if (action === "kml") setUploadDialogOpened(true);
    else if (action === "deere") handleJohnDeereImport();
    // No status pre-check for Cropwise: the dialog itself renders the
    // "connect in Settings" state, so there's no reason to round-trip first.
    else if (action === "cropwise") setCropwiseImportOpened(true);
  };

  // Field creation needs a season (useRequireSeason): without one the user is
  // prompted to create it, and the action then runs.
  const { requireSeason, seasonDialogs } = useRequireSeason<PendingFieldAction>(
    Boolean(sid),
    runFieldAction
  );

  const [visibleCount, setVisibleCount] = useState(INITIAL_BATCH);
  const importAnchorRef = useRef<HTMLDivElement>(null);
  const newFieldAnchorRef = useRef<HTMLDivElement>(null);

  // Reset visible count when fields or search changes
  useEffect(() => {
    setVisibleCount(INITIAL_BATCH);
  }, [vm.fields.length, vm.search]);

  // Progressive loading. A callback ref, so the observer follows the sentinel
  // element itself: the sentinel unmounts whenever every row is already shown
  // (e.g. a section collapsed) and comes back as a NEW node on re-expand, and
  // the old effect — keyed on the field count — kept watching the detached
  // one, so the list stopped at 30 rows. The sentinel is also keyed by
  // visibleCount (below): a fresh node is observed afresh, so a sentinel that
  // is still in view after a batch fires again instead of stalling.
  const sentinelObserver = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    sentinelObserver.current?.disconnect();
    sentinelObserver.current = null;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((prev) => prev + BATCH_INCREMENT);
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(node);
    sentinelObserver.current = observer;
  }, []);
  /**
   * Refresh which integrations are connected.
   *
   * Called when the Import menu opens rather than on mount — two extra requests
   * on every Fields load, for every user, to populate a menu most never open,
   * isn't a trade worth making.
   *
   * Stale-while-revalidate: the previous answer stays on screen while the new
   * one is in flight, so re-opening the menu doesn't flash, but connecting an
   * integration in Settings and coming straight back still updates.
   *
   * Cropwise is only queried when the feature config is on — the endpoint
   * answers 404 to everyone else, and the api client would surface that as an
   * error toast.
   */
  const refreshIntegrationStatus = () => {
    setIntegrationsChecking(true);
    const deere = getJohnDeereStatus()
      .then((s) => s.connected)
      .catch(() => false);
    const cropwise = settingsStore.hasConfig("cropwise")
      ? getCropwiseStatus()
          .then((s) => s.connected)
          .catch(() => false)
      : Promise.resolve(false);

    Promise.all([deere, cropwise])
      .then(([deereConnected, cropwiseConnected]) =>
        setIntegrationsConnected({ deere: deereConnected, cropwise: cropwiseConnected })
      )
      .finally(() => setIntegrationsChecking(false));
  };

  const toggleImportMenu = () => {
    setImportMenuOpen((prev) => {
      const next = !prev;
      if (next) refreshIntegrationStatus();
      return next;
    });
  };

  // Dismiss the popovers on outside click / Escape, like any other floating menu.
  useDismiss(importMenuOpen, importAnchorRef, () => setImportMenuOpen(false));
  useDismiss(newFieldMenuOpen, newFieldAnchorRef, () => setNewFieldMenuOpen(false));

  // "From tracks" builds boundaries out of machine telemetry, which the server
  // only serves to roles with device access, and is released per customer under
  // the `fields_from_tracks` feature config (admin → Configs). Without both, New
  // field draws straight away, exactly as before.
  const canUseTracks =
    accountStore.canAccessDevices && settingsStore.hasConfig("fields_from_tracks");
  const handleNewField = () => {
    if (canUseTracks) setNewFieldMenuOpen((open) => !open);
    else if (requireSeason("draw")) vm.handleFieldCreate();
  };

  const handleJohnDeereImport = () => {
    getJohnDeereStatus()
      .then((s) => {
        if (s.connected) {
          setJohnDeereImportOpened(true);
        } else {
          setConnectPromptVisible(true);
        }
      })
      .catch(() => {
        setConnectPromptVisible(true);
      });
  };

  const importFile = (
    features: GeoJsonFeature[],
    cropId: string,
    close: () => void = () => setUploadDialogOpened(false)
  ) =>
    vm
      .importBoundaryFeatures(features, sid, cropId)
      .then(({ imported, duplicates, failed, firstError }) => {
        // Re-uploading a file the account already holds is a normal thing to
        // do — the server refuses each repeated boundary instead of creating a
        // second copy. Say how many were skipped rather than reporting them as
        // errors, and don't claim success when nothing was actually created.
        const skipped = duplicates
          ? ` ${lang.fields.uploadDuplicatesSkipped.replace(
              "{count}",
              String(duplicates)
            )}`
          : "";
        if (imported > 0) {
          close();
          toast.success(
            `${lang.fields.uploadImportedCount.replace(
              "{count}",
              String(imported)
            )}.${skipped}`
          );
        } else if (duplicates > 0 && failed === 0) {
          close();
          toast.info(lang.fields.uploadAllDuplicates);
        }
        if (failed > 0) {
          // Stay until the user closes it — import errors need to be read/acted on.
          toast.error(firstError || lang.fields.uploadError, { autoClose: false });
        }
      })
      .catch((e) => {
        toast.error(e?.message || lang.fields.uploadError, { autoClose: false });
      });

  const fieldArea = (field: Feature) => formatArea(getFieldAreaHectares(field));

  const sortOptions: { key: TSortBy; label: string }[] = [
    { key: "name", label: lang.fields.sortByName },
    { key: "size", label: lang.fields.sortBySize },
    { key: "crop", label: lang.fields.sortByCrop },
    { key: "harvestProgress", label: lang.fields.sortByHarvest },
  ];

  return (
    <Column mainAxisSize="stretch" crossAxisSize="max">
      <Header>
        {vm.fields != null && vm.fields.length > 0 ? (
          <Column crossAxisSize="max">
            <Row mainAxisSize="stretch" justifyContent="space-between" alignItems="center">
              <Text color="#494C5B" type="heading" size={24}>
                {lang.fields.title} : {vm.fields?.length}
              </Text>
              <SelectModeButton
                active={vm.selectionMode}
                onClick={() => vm.toggleSelectionMode()}
              >
                <Checkbox checked={vm.selectionMode} />
                {vm.selectionMode
                  ? lang.fields.selectDone
                  : lang.fields.selectButton}
              </SelectModeButton>
            </Row>
            <SizedBox height={12} />
            <SearchInput
              style={{ width: "100%" }}
              value={vm.search}
              onChange={(e) => vm.setSearch(e.target.value)}
              placeholder={lang.crops.searchFill}
              icon={<SearchIcon />}
            />
            <SortRow>
              {sortOptions.map((opt) => (
                <SortChip
                  key={opt.key}
                  active={vm.sortBy === opt.key}
                  onClick={() => vm.setSortBy(opt.key)}
                >
                  {opt.label}
                </SortChip>
              ))}
            </SortRow>
          </Column>
        ) : (
          <>
            {/*<BackIcon />*/}
            <SizedBox height={16} />
            <Text color="#494C5B" type="heading" size={24}>
              {lang.fields.drawFieldsTitle}
            </Text>
            <Text style={{ marginTop: 8 }} size={15} color="#899093">
              {lang.fields.drawFiledsSubtitle}
            </Text>
          </>
        )}
      </Header>
      <Body>
        <Column crossAxisSize="max" alignItems="stretch">
          {(() => {
            const filtered = vm.fields.filter((field) =>
              (field.getProperties().name ?? "")
                .toLowerCase()
                .includes(vm.search.toLowerCase())
            );

            // Until progress lands there is nothing to group by: one plain
            // list with placeholder bars, split into the two sections once
            // the numbers arrive.
            const progressPending = vm.harvestLoading;
            const harvested = progressPending
              ? []
              : filtered.filter((f) => {
                  const hp = f.getProperties().harvest?.harvestPercentage ?? 0;
                  return hp >= HARVEST_THRESHOLD;
                });
            const notHarvested = progressPending
              ? filtered
              : filtered.filter((f) => {
                  const hp = f.getProperties().harvest?.harvestPercentage ?? 0;
                  return hp < HARVEST_THRESHOLD;
                });
            const notHarvestedHidden = notHarvestedCollapsed && !progressPending;

            const allVisible = [
              ...(harvested.length > 0 && !harvestedCollapsed ? harvested : []),
              ...(notHarvested.length > 0 && !notHarvestedHidden ? notHarvested : []),
            ];
            const harvestedShown = harvestedCollapsed ? 0 : harvested.length;
            const totalVisible = Math.min(visibleCount, allVisible.length);

            const renderField = (field: Feature) => {
              const id = field.getId();
              const props = field.getProperties();
              const harvest = props.harvest;
              const harvestedAreaStr =
                harvest?.harvestedAreaHectares != null
                  ? formatArea(harvest.harvestedAreaHectares)
                  : null;
              return id ? (
                <FieldItem key={id}>
                  <FieldRow
                    highlighted={isRecentlyUpdated(props?.stats)}
                    id={id}
                    selected={vm.selectionMode ? vm.selectedIds.has(String(id)) : vm.selectedFieldId === id}
                    title={props.name}
                    area={fieldArea(field)}
                    crop={vm.crops.find(c => c.id === props.culture_id)?.name ?? (props.crop_name || undefined)}
                    fillColor={props.stroke}
                    harvestPercent={harvest?.harvestPercentage ?? null}
                    harvestedArea={harvestedAreaStr}
                    harvestPending={progressPending && harvest == null}
                    isJohnDeereLinked={typeof props.externalId === "string" && props.externalId.startsWith("johndeere:")}
                    isCropwiseLinked={typeof props.externalId === "string" && props.externalId.startsWith("cropwise:")}
                    completed={Boolean(props.completed)}
                    selectable={vm.selectionMode}
                    selectChecked={vm.selectedIds.has(String(id))}
                    onToggleSelect={() => vm.toggleSelected(id)}
                    onClick={() => (vm.selectionMode ? vm.toggleSelected(id) : handleSelect(id))}
                    geometry={field.getGeometry()}
                    style={{ position: "relative" }}
                    extra={
                      vm.selectionMode ? null : (
                        <EditIcon
                          style={{ flexShrink: 0, cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            vm.handleFieldUpdate(id);
                          }}
                        />
                      )
                    }
                  />
                </FieldItem>
              ) : null;
            };

            const harvestedToRender = harvestedCollapsed
              ? []
              : harvested.slice(0, visibleCount);
            const remainingBudget = Math.max(0, visibleCount - harvestedShown);
            const notHarvestedToRender = notHarvestedHidden
              ? []
              : notHarvested.slice(0, remainingBudget);

            const filteredIds = filtered
              .map((f) => f.getId())
              .filter((x): x is string | number => x != null);
            const selectedCount = vm.selectedIds.size;
            const allSelected =
              filteredIds.length > 0 &&
              filteredIds.every((id) => vm.selectedIds.has(String(id)));

            return (
              <>
                {vm.selectionMode && (
                  <BulkActionsBar
                    selectedCount={selectedCount}
                    allSelected={allSelected}
                    onToggleAll={() =>
                      allSelected ? vm.clearSelection() : vm.selectAll(filteredIds)
                    }
                    onChangeCrop={() => {
                      setBulkCropId("");
                      setBulkCropDialogVisible(true);
                    }}
                    onMoveFarm={() => {
                      setBulkFarmId(farmsVM.currentFarmId ?? "");
                      setBulkMoveFarmDialogVisible(true);
                    }}
                    onExport={() => setBulkExportDialogVisible(true)}
                    onComplete={() => setBulkCompleteConfirmVisible(true)}
                    onDelete={() => setBulkDeleteConfirmVisible(true)}
                  />
                )}
                {harvested.length > 0 && (
                  <>
                    <SectionHeader onClick={() => setHarvestedCollapsed((v) => !v)}>
                      <span>
                        {lang.fields.harvestedSection}
                        <SectionCount>{harvested.length}</SectionCount>
                      </span>
                      <SectionArrow collapsed={harvestedCollapsed}>▼</SectionArrow>
                    </SectionHeader>
                    {harvestedToRender.map(renderField)}
                  </>
                )}
                {notHarvested.length > 0 && (
                  <>
                    {!progressPending && (
                      <SectionHeader onClick={() => setNotHarvestedCollapsed((v) => !v)}>
                        <span>
                          {lang.fields.notHarvestedSection}
                          <SectionCount>{notHarvested.length}</SectionCount>
                        </span>
                        <SectionArrow collapsed={notHarvestedCollapsed}>▼</SectionArrow>
                      </SectionHeader>
                    )}
                    {notHarvestedToRender.map(renderField)}
                  </>
                )}
                {totalVisible < allVisible.length && (
                  <div key={visibleCount} ref={sentinelRef} style={{ height: 1 }} />
                )}
              </>
            );
          })()}
        </Column>
        <Column crossAxisSize="max">
          <ImportAnchor ref={newFieldAnchorRef}>
            <Button
              onClick={handleNewField}
              style={{
                maxWidth: "calc(100% - 32px)",
                margin: "16px",
                flexShrink: 0,
              }}
            >
              {lang.fields.drawFieldsButton}
            </Button>
            {newFieldMenuOpen && (
              <ImportSubMenu>
                <ImportSubButton onClick={() => {
                  setNewFieldMenuOpen(false);
                  if (requireSeason("draw")) vm.handleFieldCreate();
                }}>
                  {lang.fields.newFieldDraw}
                </ImportSubButton>
                <ImportSubButton onClick={() => {
                  setNewFieldMenuOpen(false);
                  if (requireSeason("tracks")) setFromTracksOpened(true);
                }}>
                  {lang.fields.newFieldFromTracks}
                </ImportSubButton>
              </ImportSubMenu>
            )}
          </ImportAnchor>
          <ImportAnchor ref={importAnchorRef}>
            <Button
              onClick={toggleImportMenu}
              kind="outline"
              style={{
                maxWidth: "calc(100% - 32px)",
                margin: "0 16px",
                flexShrink: 0,
              }}
            >
              {lang.fields.importButton}
            </Button>
            {importMenuOpen && (
              <ImportSubMenu>
                <ImportSubButton onClick={() => {
                  setImportMenuOpen(false);
                  if (requireSeason("kml")) setUploadDialogOpened(true);
                }}>
                  {lang.fields.importKml}
                </ImportSubButton>
                {/* John Deere and Cropwise appear only while their integration is
                    connected — an entry that always leads to "go connect this
                    first" is noise. Trade-off: the Fields screen no longer
                    advertises the integrations, so Settings is the only place to
                    discover them. */}
                {integrationsConnected?.deere && (
                  <ImportSubButton onClick={() => {
                    setImportMenuOpen(false);
                    if (requireSeason("deere")) handleJohnDeereImport();
                  }}>
                    {lang.fields.importJohnDeere}
                  </ImportSubButton>
                )}
                {/* Also behind the `cropwise` feature config — allowlisted users only. */}
                {settingsStore.hasConfig("cropwise") && integrationsConnected?.cropwise && (
                  <ImportSubButton onClick={() => {
                    setImportMenuOpen(false);
                    if (requireSeason("cropwise")) setCropwiseImportOpened(true);
                  }}>
                    {lang.fields.importCropwise}
                  </ImportSubButton>
                )}
                {/* Only on the very first open, when there's no cached answer to
                    show — a revalidation keeps the previous entries visible. */}
                {integrationsChecking && integrationsConnected === null && (
                  <ImportSubNote>
                    {lang.fields.importCheckingIntegrations}
                  </ImportSubNote>
                )}
              </ImportSubMenu>
            )}
          </ImportAnchor>
          {/* "Delete All Fields" removed — bulk select → Select all → Delete
              covers it, and does so with the selection visible rather than as a
              single irreversible button sitting under the field list. */}
        </Column>
      </Body>
      <UploadFromFileDialog
        onSubmit={importFile}
        visible={uploadDialogOpened}
        onClose={() => setUploadDialogOpened(false)}
      />
      <FromTracksDialog
        visible={fromTracksOpened}
        seasonId={sid ?? ""}
        onClose={() => setFromTracksOpened(false)}
        onSubmit={(features, cropId) =>
          importFile(features, cropId, () => setFromTracksOpened(false))
        }
      />
      <ImportFromJohnDeereDialog
        visible={johnDeereImportOpened}
        onClose={() => setJohnDeereImportOpened(false)}
        onSuccess={() => vm.loadFields()}
      />
      {settingsStore.hasConfig("cropwise") && (
        <ImportFromCropwiseDialog
          visible={cropwiseImportOpened}
          onClose={() => setCropwiseImportOpened(false)}
          onSuccess={() => vm.loadFields()}
        />
      )}
      <Dialog
        title={lang.fields.bulkChangeCrop}
        visible={bulkCropDialogVisible}
        onClose={() => setBulkCropDialogVisible(false)}
        style={{ maxWidth: 400 }}
      >
        <Column crossAxisSize="max" style={{ gap: 12 }}>
          <Text size={14} color="#6B7280">
            {lang.fields.bulkChangeCropConfirm.replace("{n}", String(vm.selectedIds.size))}
          </Text>
          <Select
            label={lang.fields.newFieldCrop}
            value={bulkCropId}
            onChange={setBulkCropId}
            options={vm.crops.map((crop) => ({ title: crop.name, value: crop.id }))}
          />
          <Row justifyContent="flex-end" style={{ gap: 8, marginTop: 8 }}>
            <Button kind="outline" onClick={() => setBulkCropDialogVisible(false)}>
              {lang.fields.drawFieldsCancelButton}
            </Button>
            <Button
              disabled={!bulkCropId}
              onClick={() => {
                setBulkCropDialogVisible(false);
                vm.bulkChangeCrop(bulkCropId);
              }}
            >
              {lang.fields.bulkChangeCropApply}
            </Button>
          </Row>
        </Column>
      </Dialog>
      <Dialog
        title={lang.farms.moveToFarm}
        visible={bulkMoveFarmDialogVisible}
        onClose={() => setBulkMoveFarmDialogVisible(false)}
        style={{ maxWidth: 400 }}
      >
        <Column crossAxisSize="max" style={{ gap: 12 }}>
          <Select
            label={lang.menu.farmTitle}
            value={bulkFarmId}
            onChange={setBulkFarmId}
            options={farmsVM.farms.map((f) => ({ title: f.name, value: f.id }))}
          />
          <Row justifyContent="flex-end" style={{ gap: 8, marginTop: 8 }}>
            <Button kind="outline" onClick={() => setBulkMoveFarmDialogVisible(false)}>
              {lang.fields.drawFieldsCancelButton}
            </Button>
            <Button
              disabled={!bulkFarmId}
              onClick={() => {
                setBulkMoveFarmDialogVisible(false);
                vm.bulkMoveToFarm(bulkFarmId);
              }}
            >
              {lang.farms.moveToFarm}
            </Button>
          </Row>
        </Column>
      </Dialog>
      <Dialog
        title={lang.fields.bulkExport}
        visible={bulkExportDialogVisible}
        onClose={() => setBulkExportDialogVisible(false)}
        style={{ maxWidth: 400 }}
      >
        <Column crossAxisSize="max" style={{ gap: 12 }}>
          <Text size={14} color="#6B7280">
            {lang.fields.bulkExportConfirm.replace("{n}", String(vm.selectedIds.size))}
          </Text>
          <Row style={{ gap: 8 }}>
            <Button
              kind="outline"
              style={{ flex: 1 }}
              onClick={() => {
                setBulkExportDialogVisible(false);
                vm.bulkExport("kml");
              }}
            >
              {lang.fields.bulkExportKml}
            </Button>
            <Button
              kind="outline"
              style={{ flex: 1 }}
              onClick={() => {
                setBulkExportDialogVisible(false);
                vm.bulkExport("shp");
              }}
            >
              {lang.fields.bulkExportShp}
            </Button>
          </Row>
        </Column>
      </Dialog>
      <Dialog
        title={lang.fields.bulkDeleteTitle}
        visible={bulkDeleteConfirmVisible}
        onClose={() => setBulkDeleteConfirmVisible(false)}
        style={{ maxWidth: 400 }}
      >
        <Column crossAxisSize="max" style={{ gap: 12 }}>
          <Text size={14} color="#6B7280">
            {lang.fields.bulkDeleteConfirm.replace("{n}", String(vm.selectedIds.size))}
          </Text>
          <Row justifyContent="flex-end" style={{ gap: 8, marginTop: 8 }}>
            <Button kind="outline" onClick={() => setBulkDeleteConfirmVisible(false)}>
              {lang.fields.drawFieldsCancelButton}
            </Button>
            <Button
              onClick={() => {
                setBulkDeleteConfirmVisible(false);
                vm.bulkDelete();
              }}
              style={{ background: "#e53e3e", borderColor: "#e53e3e" }}
            >
              {lang.fields.bulkDeleteButton}
            </Button>
          </Row>
        </Column>
      </Dialog>
      <Dialog
        title={lang.fields.bulkCompleteTitle}
        visible={bulkCompleteConfirmVisible}
        onClose={() => setBulkCompleteConfirmVisible(false)}
        style={{ maxWidth: 420 }}
      >
        <Column crossAxisSize="max" style={{ gap: 12 }}>
          <Text size={14} color="#6B7280">
            {lang.fields.bulkCompleteConfirm.replace("{n}", String(vm.selectedIds.size))}
          </Text>
          <Row justifyContent="flex-end" style={{ gap: 8, marginTop: 8 }}>
            <Button kind="outline" onClick={() => setBulkCompleteConfirmVisible(false)}>
              {lang.fields.drawFieldsCancelButton}
            </Button>
            <Button
              onClick={() => {
                setBulkCompleteConfirmVisible(false);
                vm.bulkComplete();
              }}
            >
              {lang.fields.bulkCompleteButton}
            </Button>
          </Row>
        </Column>
      </Dialog>
      <Dialog
        title="John Deere not connected"
        visible={connectPromptVisible}
        onClose={() => setConnectPromptVisible(false)}
        style={{ maxWidth: 400 }}
      >
        <Column crossAxisSize="max" style={{ gap: 12 }}>
          <Text size={14} color="#6B7280">
            You need to connect your John Deere account first. Go to Settings to set up the connection?
          </Text>
          <Row justifyContent="flex-end" style={{ gap: 8, marginTop: 8 }}>
            <Button kind="outline" onClick={() => setConnectPromptVisible(false)}>
              Cancel
            </Button>
            <Button onClick={() => { setConnectPromptVisible(false); navigate("/settings"); }}>
              Go to Settings
            </Button>
          </Row>
        </Column>
      </Dialog>
      {seasonDialogs}
    </Column>
  );
};
export default observer(ViewFieldsMenu);
